import { logSwallowed } from '@/lib/errors';
// ============================================================================
// ChatThread.tsx — переписка чата (ЕДИНЫЙ компонент для ChatPage и
// контекстных обсуждений): лента сообщений, отправка (Enter/кнопка),
// вложения, ответ, реакции, пересылка, «печатает…», галочки ✓✓,
// пресенс, очистка (У себя/У всех/Окончательно — админ).
// Realtime — событийная модель (eventBusHub: chat.message.created.{convId} /
// read / reacted / typing), НИКАКИХ таймеров-поллинга. SSoT — сервер.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { ArrowLeft, ArrowDown, X, MessageSquare, Paperclip, Search, Mic, Square, Send, Phone, Monitor, Link2, MapPin, Info, Star, Headphones, Users, MoreVertical, RotateCcw, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { listMessages as bridgeListMessages, listMessagesBefore as bridgeListMessagesBefore, markRead as bridgeMarkRead, reactToMessage as bridgeReact, forwardMessage as bridgeForward, deleteMessage as bridgeDelete, clearConversation as bridgeClear, pinMessage as bridgePin, getConversation as bridgeGetConversation } from '@/lib/chatBridge';
import { useMentionSuggest } from '@/components/chat/useMentionSuggest';
import { useChatDraft } from '@/components/chat/useChatDraft';
import { ChatMentionSuggest } from '@/components/chat/ChatMentionSuggest';
import { AppEmbedPreview } from '@/components/embed/AppEmbedPreview';
import { createLogger } from '@/lib/logger';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { confirmDialog } from '@/lib/dialogs';
import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useIsDarkTheme } from '@/hooks/useIsDarkTheme';
import { useConnectionStore } from '@/lib/connection';
import { sendChatMessage, useChatQueueStore } from '@/lib/chatQueue';
import { setAutoUpdateBusy } from '@/lib/autoUpdate';
import { uploadFile } from '@/lib/chunkedUpload';
import { isVoiceMessage } from '@/lib/fileName';
import { startStt, type SttSession } from '@/lib/stt';
import { useSttAvailability } from '@/lib/useSttAvailability';
import { createTranscriptBuffer } from '@/lib/voice/sttTranscript';
import { useToastStore } from '@/store/toastStore';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { fileBytes } from '@/lib/fileBytes';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { ChatAttachmentComposer } from '@/components/chat/ChatAttachmentComposer';
import { validateChatAttachmentFiles } from '@/components/chat/chatAttachmentLimits';
import { Virtuoso, type VirtuosoHandle, type ListRange } from 'react-virtuoso';
import { MessageBubble, type Msg as BubbleMsg } from '@/components/chat/MessageBubble';
import { VideoCallPanel } from '@/components/chat/VideoCallPanel';
import { InviteLinkDialog } from '@/components/chat/InviteLinkDialog';
import { RemotePanel } from '@/components/remote/RemotePanel';
import { publishRemoteSignal, remoteTopic } from '@/lib/remote/remoteSession';
import { GeoMessageComposer } from '@/components/geo/GeoMessageComposer';
import { formatGeoMessage, type GeoMessagePoint } from '@/lib/geo/geoMessage';
import { ChatInfoPanel } from '@/components/chat/ChatInfoPanel';
import { AssistantContactsPanel } from '@/components/chat/AssistantContactsPanel';
import { ASSISTANT_BOT_LOGIN, isAssistantBotLogin, resolveEntityProfile } from '@/lib/contacts/contactService';
import { isPreviewableAttachment } from '@/components/chat/chatInfoData';
import { openConversation } from '@/lib/chatNavigation';
import { copyToClipboard } from '@/lib/clipboard';
import { useAppContextMenu, type AppMenuItem } from '@/lib/appContextMenu';
import { ChatSelectionBar } from '@/components/chat/chatSelection';
import { messageIdAt, useMessageSelection } from '@/components/chat/useMessageSelection';
import { ensureFavoritesChat } from '@/lib/chatFavorites';
import { sendToAssistant } from '@/lib/assistantChat';
import { stripAssistantSources } from '@/lib/assistantSources';
import { ChatSearchPanel } from '@/components/chat/ChatSearchPanel';
import { CHAT_FOCUS_INPUT_EVENT, consumeChatInputFocus } from '@/lib/notifications/notificationActions';
import { settingsStore } from '@/lib/settingsStore';

const log = createLogger('ChatThread');

// Русские названия типов контекста обсуждения (для запроса ассистенту).
const OBJECT_CONTEXT_LABELS: Record<string, string> = {
  catalog: 'Справочник',
  document: 'Документ',
  order: 'Заявка',
  task: 'Задача',
  register: 'Регистр',
  poll: 'Тест',
  support: 'Обращение',
};

export interface ThreadUser { id: string; name: string; avatarUrl?: string; login?: string; kind?: string; isBot?: boolean }

export interface ForwardTarget { id: string; title: string }

interface FileRef { id: string; name: string; size: number; fileType?: string }
interface MsgReaction { emoji: string; userId: string }

interface Msg {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  createdAt: string;
  isDeleted?: boolean;
  readBy?: string[];
  attachments?: FileRef[];
  replyTo?: string;
  reactions?: MsgReaction[];
  /** D2: отметка «изменено» + история правок (SSoT — сервер) */
  editedAt?: string;
  editHistory?: { text: string; editedAt: string }[];
  /** D3: число ответов треда («N ответов» под сообщением) */
  replyCount?: number;
  /** D6: закреплено в чате (правая панель «Закреплённые») */
  isPinned?: boolean;
}

// ЕДИНЫЙ маппинг сообщения сервера → Msg (лента/подгрузка/переход из поиска).
function mapMsg(m: any): Msg {
  return {
    id: m.id,
    conversationId: m.conversationId,
    senderId: m.senderId,
    text: m.text || '',
    createdAt: m.createdAt,
    isDeleted: !!m.isDeleted,
    readBy: m.readBy || [],
    attachments: (m.attachments || []).map((a: any) => ({ id: a.id, name: a.name || '—', size: Number(a.size || 0), fileType: String(a.fileType || a.file_type || '') })),
    replyTo: m.replyTo || '',
    reactions: (m.reactions || []).map((r: any) => ({ emoji: r.emoji, userId: r.userId })),
    editedAt: m.editedAt || '',
    editHistory: (m.editHistory || []).map((h: any) => ({ text: h.text || '', editedAt: h.editedAt || '' })),
    replyCount: Number(m.replyCount || 0),
    isPinned: !!m.isPinned,
  };
}

// Пресенс собеседника: «онлайн» (вход ≤2 мин назад) / «был(а) …»
import { lastSeenLabel, QueuedIndicator } from './chatThreadParts';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { useOfflineDataStore } from '@/lib/offlineCache';
import { useMediaAutoplay } from '@/components/chat/useMediaAutoplay';
import { setAutoplayNext, useMediaSettings } from '@/lib/mediaSettings';
interface ChatThreadProps {
  conversationId: string;
  title: string;
  /** Пресенс участников: id → last_seen_at (SSoT — сервер) */
  memberLastSeen?: Record<string, string>;
  /** Участники чата (для правой панели; если нет — берутся из getConversation) */
  memberIds?: string[];
  /** Контекст объектного чата (type+code+id) — блок «О сущности» правой панели */
  contextType?: string;
  contextCode?: string;
  contextId?: string;
  /** Открыть диалог из панели (написать участнику); без обработчика — навигация */
  onOpenConversation?: (convId: string) => void;
  /** Имена пользователей (если не переданы — грузятся сами через listEntities) */
  users?: ThreadUser[];
  myId: string;
  isAdmin: boolean;
  /** Чаты для пересылки (если не передано — кнопка «Переслать» скрыта) */
  forwardTargets?: ForwardTarget[];
  /** id чата «Избранное» (если известен; иначе резолвится при действии) */
  favoritesConversationId?: string;
  /** Открыт сам чат избранного (быстрое действие «В избранное» скрыто) */
  isFavorites?: boolean;
  /** Кнопка «←» в шапке (мобильный список / контекстная панель) */
  onBack?: () => void;
  /** Авто-прочтение при открытии (default true) */
  markReadOnOpen?: boolean;
  /** «Покинуть группу» (панель «О чате», §2.3): родитель закрывает чат
   *  (ChatPage — к списку; контекстная панель — закрывается целиком). */
  onConversationLeft?: () => void;
  /** Переход из поиска ВСЕХ чатов: открыть сообщение в ДРУГОМ чате
   *  (родитель переключает чат и передаёт focusMessageId). */
  onOpenMessage?: (conversationId: string, messageId: string, query: string) => void;
  /** Открыть конкретное сообщение при монтировании (переход из поиска). */
  focusMessageId?: string;
  /** Запрос, по которому сообщение найдено (подсветка совпадения). */
  focusQuery?: string;
  /** Переход выполнен — родитель сбрасывает focusMessageId. */
  onFocusConsumed?: () => void;
}

export function ChatThread({
  conversationId, title, memberLastSeen, memberIds, contextType, contextCode, contextId,
  onOpenConversation, users: usersProp, myId, isAdmin, forwardTargets, favoritesConversationId, isFavorites, onBack, markReadOnOpen = true,
  onConversationLeft, onOpenMessage, focusMessageId, focusQuery, onFocusConsumed,
}: ChatThreadProps) {
  const { t } = useI18n();
  // Правая панель «О чате» (контекстная карточка сущности/участники/файлы).
  const [infoOpen, setInfoOpen] = useState(false);
  // Контакты пользователя в ЧАТЕ АССИСТЕНТА: единый каталог + карточка
  // контакта (Telegram/MAX/внутренний чат/профиль). Панель и «О чате»
  // взаимоисключающие — лента не должна сжиматься двумя колонками сразу.
  const [contactsOpen, setContactsOpen] = useState(false);
  // Колонка справа возможна только в широком контейнере; в панели обсуждения
  // или узком чат-виджете — прежний выезд поверх (лента не сжимается в ноль).
  const rootRef = useRef<HTMLDivElement>(null);
  const [narrowPanel, setNarrowPanel] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setNarrowPanel(el.clientWidth > 0 && el.clientWidth < 640);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // D3: обсуждение под сообщением (корень + прямые ответы; ответ уходит в тред).
  const [threadRoot, setThreadRoot] = useState<Msg | null>(null);
  const [threadItems, setThreadItems] = useState<Msg[]>([]);
  const [threadText, setThreadText] = useState('');
  const [threadBusy, setThreadBusy] = useState(false);

  const mapThreadMsg = (m: any): Msg => ({
    id: m.id, conversationId: m.conversationId, senderId: m.senderId,
    text: m.text || '', createdAt: m.createdAt, isDeleted: !!m.isDeleted,
    readBy: m.readBy || [], attachments: [], replyTo: m.replyTo || '',
    reactions: (m.reactions || []).map((r: any) => ({ emoji: r.emoji, userId: r.userId })),
    editedAt: m.editedAt || '', replyCount: Number(m.replyCount || 0),
  });

  const openThread = async (mm: Msg) => {
    setThreadRoot(mm);
    setThreadItems([mapThreadMsg(mm)]);
    setThreadText('');
    try {
      const res: any = await (getSDK() as any).messenger.listThread({ conversationId, rootMessageId: mm.id, limit: 100 });
      setThreadItems((res?.items || []).map(mapThreadMsg));
    } catch (e) {
      log.error('Чат: тред не загружен', e);
    }
  };

  const sendThread = async () => {
    if (!threadRoot) return;
    const replyText = threadText.trim();
    if (!replyText) return;
    setThreadBusy(true);
    try {
      const r = await sendChatMessage({ conversationId, text: replyText, attachments: [], replyTo: threadRoot.id }, myId);
      setThreadText('');
      if (r.queued) {
        useToastStore.getState().showInfo(t('chat.offline_reply'));
      } else {
        const res: any = await (getSDK() as any).messenger.listThread({ conversationId, rootMessageId: threadRoot.id, limit: 100 });
        setThreadItems((res?.items || []).map(mapThreadMsg));
        setMessages((prev) => prev.map((x) => (x.id === threadRoot.id ? { ...x, replyCount: (x.replyCount || 0) + 1 } : x)));
      }
    } catch (e) {
      log.error('Чат: ответ в тред не отправлен', e);
    } finally {
      setThreadBusy(false);
    }
  };

  const [messages, setMessages] = useState<Msg[]>([]);
  // Первичная загрузка ленты: до ответа сервера НЕ показываем «Сообщений нет»
  // (иначе пустое состояние мигает и вводит в заблуждение на медленной сети).
  const [initialLoading, setInitialLoading] = useState(true);
  // «Новый диалог» ассистента: очистка идёт (кнопка/пункт меню недоступны).
  const [resetBusy, setResetBusy] = useState(false);
  // Личный чат ассистента: помеченные «у всех» сообщения (после «Нового
  // диалога») НЕ показываем плейсхолдерами — лента должна быть чистой.
  // Ref читают асинхронные загрузчики (isAssistantChat вычисляется позже).
  const isAssistantChatRef = useRef(false);
  // D2: правка своего сообщения (диалог с текстом; история — на сервере).
  const [editMsg, setEditMsg] = useState<Msg | null>(null);
  const [editText, setEditText] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const doEdit = async () => {
    if (!editMsg) return;
    const text = editText.trim();
    if (!text) return;
    setEditBusy(true);
    try {
      await (getSDK() as any).messenger.editMessage({
        conversationId: editMsg.conversationId, messageId: editMsg.id, text,
      });
      // Локально сразу: текст + отметка + история (событие сервера обновит других).
      const now = new Date().toISOString();
      setMessages((prev) => prev.map((x) => (x.id === editMsg.id
        ? { ...x, text, editedAt: now, editHistory: [...(x.editHistory || []), { text: x.text, editedAt: now }] }
        : x)));
      setEditMsg(null);
    } catch (e) {
      log.error('Чат: правка сообщения не выполнена', e);
    } finally {
      setEditBusy(false);
    }
  };
  const [text, setText] = useState('');
  // ЧЕРНОВИК сообщения (user_settings chat.draft.<id>): восстановление при
  // открытии чата, автосохранение при вводе (500 мс), сохранение при уходе.
  useChatDraft(conversationId, text, setText);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // Офлайн-фёрст: лента открыта из сохранённого кэша (chatBridge.readThrough) —
  // единый баннер «данные из кэша» (ключ совпадает с ключом чтения).
  const chatOfflineKey = `chat:messages:${conversationId}`;
  const chatOffline = useOfflineDataStore((s) => !!s.stale[chatOfflineKey]);
  // firstItemIndex — КАНонический паттерн Virtuoso для ПРЕПЕНДА (loadOlder):
  // без него сдвиг всех индексов = прыжок скролла на высоту подгруженных сообщений
  const FIRST_ITEM_INDEX_START = 1_000_000;
  const [firstItemIndex, setFirstItemIndex] = useState(FIRST_ITEM_INDEX_START);
  // МЕСТО ЧТЕНИЯ чата (как в Telegram): id верхнего видимого сообщения +
  // признак «у низа» в user_settings (`chat.read.<id>`); дебаунс — не на каждый
  // пиксель. При возврате в чат место восстанавливается ДО автоскролла.
  interface ReadPos { id: string; atBottom: boolean; updatedAt?: string }
  const readPosKey = `chat.read.${conversationId}`;
  const readPosRef = useRef<ReadPos | null>(null);
  const readSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoredRef = useRef(false);
  const suppressAutoScrollRef = useRef(true);
  const atBottomRef = useRef(true);
  const didInitialScroll = useRef(false);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSent = useRef(0);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  // Пересылка: id сообщений (одиночная — 1 элемент, пакетная — из режима выбора)
  const [forwardIds, setForwardIds] = useState<string[]>([]);
  // «Избранное» (заметки себе): быстрая пересылка в чат «только я»
  const [favBusy, setFavBusy] = useState(false);
  const inFavoritesChat = !!isFavorites || (!!favoritesConversationId && favoritesConversationId === conversationId);
  // Множественный выбор сообщений (§2.3): режим/выбранные + делегированные
  // жесты ленты (удержание 500 мс → контекстное меню, тап — выбор/снятие).
  // Меню открывается через ref: openMessageMenu объявлен ниже (TDZ), а жест
  // вызывается уже после рендера.
  const messagesRef = useRef<Msg[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const openMessageMenuRef = useRef<(target: Msg, x: number, y: number) => void>(() => undefined);
  const handleLongPress = useCallback((id: string, x: number, y: number) => {
    const target = messagesRef.current.find((m) => m.id === id);
    if (target) openMessageMenuRef.current(target, x, y);
  }, []);
  const {
    selectMode, selectedIds, enterSelect, exitSelect, toggleSelect,
    onPointerDown: onFeedPointerDown, onPointerMove: onFeedPointerMove,
    onPointerUp: onFeedPointerUp, onPointerCancel: onFeedPointerCancel, onClickOutside: onFeedClickOutside,
  } = useMessageSelection(handleLongPress);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileRef[]>([]);
  // Композер вложений: выбор файлов открывает шторку (не отправляет сразу).
  const [attachComposer, setAttachComposer] = useState<{ files: File[]; warnings: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<ThreadUser[]>(usersProp || []);
  const [searchOpen, setSearchOpen] = useState(false);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [videoCallId, setVideoCallId] = useState('');
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [remoteOpen, setRemoteOpen] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  // ПОИСК: встроенная панель (без окон) — свой чат / все чаты, фильтры,
  // подсветка и переход к найденному сообщению. SSoT — серверный SearchMessages.
  const [searchHighlight, setSearchHighlight] = useState('');
  const [flashMessageId, setFlashMessageId] = useState('');
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDark = useIsDarkTheme();
  // «Слушать подряд»: очередь аудио ленты + ожидаемый автозапуск. Прокрутка —
  // в АБСОЛЮТНЫХ индексах Virtuoso (itemContent отдаёт индекс с firstItemIndex).
  const firstItemIndexRef = useRef(firstItemIndex);
  firstItemIndexRef.current = firstItemIndex;
  const scrollToMediaMessage = useCallback((rel: number) => {
    virtuosoRef.current?.scrollToIndex({ index: rel + firstItemIndexRef.current, align: 'center' });
    return true;
  }, []);
  const { autoplayFileId, handleAudioEnded, handleAudioInterrupt, handleAutoplayConsumed } = useMediaAutoplay(messages, scrollToMediaMessage);
  const { autoplayNext } = useMediaSettings();

  // Имена пользователей (SSoT — сервер); внешний users имеет приоритет
  useEffect(() => {
    if (usersProp && usersProp.length > 0) {
      setUsers(usersProp);
      return;
    }
    type EntityRow = {
      id?: unknown; name?: unknown; login?: unknown; avatarUrl?: unknown; avatar_url?: unknown;
      kind?: unknown; isBot?: unknown; is_bot?: unknown;
    };
    getSDK().entity.listEntities({})
      .then((r) => {
        const items = (r as { items?: EntityRow[] }).items || [];
        setUsers(items.map((u) => ({
          id: String(u.id || ''),
          name: String(u.name || u.login || u.id || ''),
          avatarUrl: String(u.avatarUrl || u.avatar_url || ''),
          login: String(u.login || ''),
          kind: String(u.kind || ''),
          isBot: !!(u.isBot ?? u.is_bot),
        })));
      })
      .catch((e) => { log.warn('users load failed', e); });
  }, [usersProp]);

  // --- Упоминания «@»: участники диалога, автодополнение и подсветка ---
  // memberIds приходит снаружи (ChatPage); контекстные панели/виджеты не
  // передают его — участники берутся из диалога (SSoT сервер, offline-кэш).
  const [mentionMembers, setMentionMembers] = useState<string[]>(memberIds || []);
  useEffect(() => {
    if (memberIds && memberIds.length > 0) { setMentionMembers(memberIds); return; }
    let alive = true;
    bridgeGetConversation(conversationId)
      .then((c) => { if (alive) setMentionMembers(c.memberIds || []); })
      .catch((e: unknown) => { logSwallowed(e, 'components/chat/ChatThread.tsx:mentions'); return undefined; });
    return () => { alive = false; };
  }, [conversationId, memberIds]);

  // Логины участников для подсветки (стабильный массив — MessageBubble под memo)
  const mentionLogins = useMemo(() => {
    const members = mentionMembers.length > 0 ? new Set(mentionMembers) : null;
    return users
      .filter((u) => !!u.login && (!members || members.has(u.id)))
      .map((u) => String(u.login));
  }, [users, mentionMembers]);
  const myLogin = useMemo(() => users.find((u) => u.id === myId)?.login || '', [users, myId]);

  const mentionInputRef = useRef<HTMLInputElement>(null);
  const mention = useMentionSuggest({
    value: text,
    people: users,
    memberIds: mentionMembers,
    inputRef: mentionInputRef,
    setValue: setText,
  });

  // «Ответить» из карточки уведомления: фокус в поле ввода. Запрос ставит
  // notificationActions; чат может открыться позже (проверяем при монтировании/
  // смене диалога) или уже быть открытым (событие шины, без перемонтирования).
  useEffect(() => {
    const focusInput = () => {
      if (!consumeChatInputFocus(conversationId)) return;
      window.requestAnimationFrame(() => mentionInputRef.current?.focus());
    };
    focusInput();
    const onRequest = (e: Event) => {
      const id = String((e as CustomEvent<{ convId?: string }>).detail?.convId || '');
      if (id === conversationId) focusInput();
    };
    window.addEventListener(CHAT_FOCUS_INPUT_EVENT, onRequest);
    return () => window.removeEventListener(CHAT_FOCUS_INPUT_EVENT, onRequest);
  }, [conversationId]);

  // Фон ЧАТА — per-chat (как в Telegram) + fallback глобальный
  const [chatBg, setChatBg] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      const css = await import('@/lib/wallpaper').then((m) => m.wallpaperCssForChat(conversationId)).catch(() => '');
      if (!cancelled) setChatBg(css);
    };
    void apply();
    const onWall = () => void apply();
    const onWallChat = () => void apply();
    window.addEventListener('hydra:wallpaper-chat' as any, onWall);
    window.addEventListener(('hydra:wallpaper-chat:' + conversationId) as any, onWallChat);
    return () => { cancelled = true; window.removeEventListener('hydra:wallpaper-chat' as any, onWall); window.removeEventListener(('hydra:wallpaper-chat:' + conversationId) as any, onWallChat); };
  }, [conversationId]);

  // Единый маппинг ленты: в чате ассистента скрываем «удалено у всех»
  // (после «Нового диалога» это вся прежняя переписка — чистая лента).
  const mapThreadMessages = useCallback((raw: any[]): Msg[] => {
    const list = raw.map(mapMsg);
    return isAssistantChatRef.current ? list.filter((m) => !m.isDeleted) : list;
  }, []);

  const loadMessages = useCallback(async (replace = false) => {
    try {
      const fresh = mapThreadMessages((await bridgeListMessages(conversationId, 200)) as any[]);
      setMessages((prev) => {
        if (replace || prev.length === 0) return fresh;
        // МЁРДЖ: сохраняем существующие id (стабильная идентичность → Virtuoso не пересобирает → нет скачка)
        const existing = new Map(prev.map((m) => [m.id, m]));
        for (const m of fresh) existing.set(m.id, m);
        return Array.from(existing.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
      setError(null);
    } catch (e: any) {
      // ВСЕ ошибки чата — в журнал регистрации (категория ui, details — причина)
      // + видимое сообщение. «conversation not found» и пр. всегда разбираемы.
      setError(friendlyError(e));
      void import('@/lib/eventLog').then((m) => {
        m.eventLog.error('ui', 'Чат: загрузка сообщений: ' + (e?.message || String(e)), {
          details: e?.stack || '',
        }).catch((e) => { logSwallowed(e, 'components/chat/ChatThread.tsx:166'); return undefined; });
      });
    } finally {
      setInitialLoading(false);
    }
  }, [conversationId]);

  // БЕСКОНЕЧНАЯ ЗАГРУЗКА «старых» сообщений: страница до before_ts первого
  // (Virtuoso atTopStateChange, НЕ таймер)
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const loadingOlderRef = useRef(false);
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasOlder) return;
    const first = messages[0];
    if (!first) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const older = mapThreadMessages((await bridgeListMessagesBefore(conversationId, first.createdAt, 100)) as any[]);
      if (older.length === 0) { setHasOlder(false); return; }
      // firstItemIndex ↓ на число добавленных СВЕРХУ — Virtuoso сохраняет
      // позицию скролла (пользователь «скользит» по истории, без прыжка)
      setFirstItemIndex((idx) => idx - older.length);
      setMessages((prev) => [...older, ...prev]);
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [conversationId, messages, hasOlder]);

  // «Слушать подряд»: смена чата или включение поиска (лента фильтруется —
  // индексы и состав меняются) снимают запланированный автозапуск; уже
  // играющий плеер не останавливается.
  useEffect(() => {
    handleAudioInterrupt();
  }, [conversationId, handleAudioInterrupt]);

  // Первичная загрузка + прочтение (mark — РАЗОВО, не в loadMessages: иначе
  // mark → событие read → перезагрузка → mark → цикл)
  useEffect(() => {
    setMessages([]);
    setInitialLoading(true);
    setError(null);
    setHasOlder(true);
    loadingOlderRef.current = false;
    setFirstItemIndex(FIRST_ITEM_INDEX_START);
    didInitialScroll.current = false;
    // Место чтения: до восстановления автоскролл выключен (иначе followOutput
    // уводит вниз раньше, чем прочитана сохранённая позиция).
    restoredRef.current = false;
    suppressAutoScrollRef.current = true;
    readPosRef.current = null;
    atBottomRef.current = true;
    if (readSaveTimerRef.current) {
      clearTimeout(readSaveTimerRef.current);
      readSaveTimerRef.current = null;
    }
    // Черновик текста восстанавливает useChatDraft (user_settings
    // chat.draft.<id>) — повторная загрузка здесь затирала бы ввод.
    setReplyTo(null);
    setPendingFiles([]);
    setForwardIds([]);
    setTypingUser(null);
    exitSelect();
    cancelRecording();
    void loadMessages(true);
    if (markReadOnOpen) {
      void bridgeMarkRead(conversationId).catch((e: unknown) => log.warn('markRead', { error: String((e as Error)?.message || e) }));
    }
  }, [conversationId, loadMessages, markReadOnOpen, exitSelect]);

  // Автореконнект данных (SSoT подключения): связь восстановилась → перезагрузка ленты
  const status = useConnectionStore((s) => s.status);
  const prevStatus = useRef(status);
  useEffect(() => {
    const wasDown = prevStatus.current !== 'online';
    prevStatus.current = status;
    if (status === 'online' && wasDown) void loadMessages();
  }, [status, loadMessages]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2000) return;
    lastTypingSent.current = now;
    getSDK().eventBus.publish({
      topic: `chat.typing.${conversationId}`,
      payload: JSON.stringify({ user_id: myId, conversation_id: conversationId }),
    }).catch((e) => { logSwallowed(e, 'components/chat/ChatThread.tsx:253'); return undefined; });
  };

  // --- Звонок: серверная запись звонка (CallService) + «входящий» сигнал шины ---
  const myName = users.find((u) => u.id === myId)?.name || '';
  // Механизм ядра: звонок живёт на СЕРВЕРЕ (состояние + событие адресатам +
  // Web Push в ЗАКРЫТОЕ приложение получателя). Адресатов (участников чата)
  // определяет сервер по комнате conv_<id>; локальная публикация
  // chat.call.started остаётся — мгновенный показ у уже открытых клиентов.
  const startCall = () => {
    if (videoCallOpen) { setVideoCallOpen(false); return; }
    setVideoCallId('');
    void getSDK().calls.startCall({ room: `conv_${conversationId}`, kind: 'video' })
      .then((session) => setVideoCallId(session.id))
      .catch((e: unknown) => log.warn('call.startCall failed', { error: String((e as Error)?.message || e) }));
    getSDK().eventBus.publish({
      topic: `chat.call.started.${conversationId}`,
      payload: JSON.stringify({ user_id: myId, name: myName, members: users.map((u) => u.id), v: __APP_VERSION__ }),
    }).catch((e: unknown) => log.warn('call.started publish failed', { error: String((e as Error)?.message || e) }));
    setVideoCallOpen(true);
  };

  // --- Удалённый доступ: запрос «показать экран» участникам чата (комната remote_chat-{id}) ---
  const startRemote = () => {
    if (remoteOpen) { setRemoteOpen(false); return; }
    const sessionId = `chat-${conversationId}`;
    publishRemoteSignal(remoteTopic.started(sessionId), { user_id: myId, name: myName, members: users.map((u) => u.id) });
    setRemoteOpen(true);
  };

  // Realtime (событийная модель): лента обновляется ТОЧЕЧНО по событиям
  // конкретного чата (типинг/новое/прочтение/реакция — без поллинга)
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      if (ev.topic.startsWith('chat.typing.')) {
        const convId = ev.topic.slice('chat.typing.'.length);
        const uid = ev.payload?.user_id;
        if (convId === conversationId && uid && uid !== myId) {
          setTypingUser(users.find((u) => u.id === uid)?.name || String(uid).slice(0, 8));
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingUser(null), 3000);
        }
        return;
      }
      if (ev.topic.startsWith('chat.message.created.')) {
        const convId = ev.topic.slice('chat.message.created.'.length);
        if (convId !== conversationId) return;
        const payload = ev.payload || {};
        const hasAtts = Array.isArray(payload.attachments) && payload.attachments.length > 0;
        setMessages((prev) => {
          if (prev.some((m) => m.id === payload.id)) return prev;
          const atts: FileRef[] = hasAtts ? (payload.attachments as any[]).map((id: any) => ({ id: String(id), name: '—', size: 0 })) : [];
          return [...prev, {
            id: String(payload.id || ''),
            conversationId,
            senderId: String(payload.sender_id || ''),
            text: String(payload.text || ''),
            createdAt: String(payload.created_at || ''),
            isDeleted: false,
            readBy: payload.sender_id ? [String(payload.sender_id)] : [],
            attachments: atts,
            replyTo: String(payload.reply_to || ''),
            reactions: [],
          }];
        });
        if (hasAtts) void loadMessages();
        // Пока пользователь НЕ у нижнего края, не помечаем новые сообщения
        // прочитанными — место чтения сохраняется, кнопка «вниз» показывает
        // число непрочитанных. Отметка — при возврате к низу (эффект atBottom).
        if (String(payload.sender_id || '') !== myId && atBottomRef.current) {
          void bridgeMarkRead(conversationId).catch((e: unknown) => log.warn('markRead', { error: String((e as Error)?.message || e) }));
        }
      } else if (ev.topic.startsWith('chat.message.read.')) {
        const convId = ev.topic.slice('chat.message.read.'.length);
        const reader = ev.payload?.user_id;
        if (convId === conversationId && reader && reader !== myId) {
          setMessages((prev) => prev.map((m) => (
            m.readBy?.includes(reader) ? m : { ...m, readBy: [...(m.readBy || []), reader] }
          )));
        }
      } else if (ev.topic.startsWith('chat.message.reacted.')) {
        const convId = ev.topic.slice('chat.message.reacted.'.length);
        const p = ev.payload || {};
        if (convId === conversationId && p.message_id) {
          setMessages((prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            const reactions = (m.reactions || []).filter((r) => !(r.userId === p.user_id && r.emoji === p.emoji));
            if (p.added) reactions.push({ emoji: p.emoji, userId: p.user_id });
            return { ...m, reactions };
          }));
        }
      } else if (ev.topic.startsWith('chat.message.pinned.')) {
        // D6: закрепление/снятие (SSoT — сервер) — точечное обновление бабла;
        // панель «Закреплённые» слушает то же событие сама (ChatPinnedBlock).
        const convId = ev.topic.slice('chat.message.pinned.'.length);
        const p = ev.payload || {};
        if (convId === conversationId && p.message_id) {
          setMessages((prev) => prev.map((m) => (m.id === p.message_id ? { ...m, isPinned: !!p.pinned } : m)));
        }
      }
    });
    return () => {
      unsub();
      if (typingTimer.current) clearTimeout(typingTimer.current);
    };
  }, [conversationId, myId, users, loadMessages]);

  // Выбор/вставка/перетаскивание файлов НЕ отправляют сообщение:
  // открывается композер (превью, комментарий, «Отправить»/«Отменить»).
  const openAttachmentComposer = (files: File[]) => {
    if (files.length === 0) return;
    const { accepted, warnings } = validateChatAttachmentFiles(files, pendingFiles.length);
    if (accepted.length === 0) {
      setError(warnings.join('; ') || t('chat.no_files'));
      return;
    }
    setError(null);
    setAttachComposer({ files: accepted, warnings });
  };

  const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    openAttachmentComposer(files);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const files: File[] = [];
    for (const it of items) {
      if (it.kind !== 'file') continue;
      const f = it.getAsFile();
      if (!f) continue;
      // Вставка из буфера часто без имени — даём детерминированное имя.
      files.push(f.name ? f : new File([f], `paste-${Date.now()}.png`, { type: f.type || 'image/png' }));
    }
    if (files.length === 0) return;
    e.preventDefault();
    openAttachmentComposer(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    openAttachmentComposer(Array.from(e.dataTransfer.files));
  };

  const downloadAttachment = useCallback(async (id: string, name: string) => {
    try {
      const r: any = await getSDK().files.downloadFile({ id });
      const bytes = fileBytes(r?.data);
      if (bytes.length === 0) throw new Error(t('chat.empty_file'));
      const blob = new Blob([bytes as unknown as BlobPart]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name || 'file';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (err: any) {
      setError(friendlyError(err));
    }
  }, []);

  // Этап 5 §18: просматриваемые типы → FilePreviewModal (SSoT рендера файлов),
  // остальные — скачивание как раньше.
  const [previewAtt, setPreviewAtt] = useState<{ id: string; name: string } | null>(null);
  const handleAttachment = useCallback((id: string, name: string) => {
    if (isPreviewableAttachment(name)) setPreviewAtt({ id, name });
    else void downloadAttachment(id, name);
  }, [downloadAttachment]);

  const toggleReact = useCallback(async (m: Msg, emoji: string) => {
    try {
      await bridgeReact(conversationId, m.id, emoji);
      setMessages((prev) => prev.map((x) => {
        if (x.id !== m.id) return x;
        const reactions = (x.reactions || []).filter((r) => !(r.userId === myId && r.emoji === emoji));
        reactions.push({ emoji, userId: myId });
        return { ...x, reactions };
      }));
    } catch (err: any) {
      setError(friendlyError(err));
    }
  }, [conversationId, myId]);

  // D6: закрепить/открепить (участник чата; идемпотентно). Локально сразу —
  // событие сервера обновит других и панель «Закреплённые».
  const togglePin = useCallback(async (m: Msg, pinned: boolean) => {
    try {
      await bridgePin(conversationId, m.id, pinned);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isPinned: pinned } : x)));
    } catch (err) {
      setError(friendlyError(err));
      logErrorToJournal(err, 'Чат: закрепление сообщения');
    }
  }, [conversationId]);

  // Пересылка — ЕДИНЫЙ диалог для одиночной (из бабла) и пакетной (§2.3) пересылки.
  const forwardOne = useCallback((m: Msg) => setForwardIds([m.id]), []);
  const doForward = async (toConvId: string): Promise<boolean> => {
    if (forwardIds.length === 0) return false;
    try {
      for (const id of forwardIds) await bridgeForward(conversationId, id, toConvId);
      setForwardIds([]);
      exitSelect();
      return true;
    } catch (err: unknown) {
      setError(friendlyError(err));
      return false;
    }
  };

  // «В избранное» (заметки себе): быстрая пересылка одного или нескольких
  // сообщений в чат «только я»; текст и вложения копирует СЕРВЕР (forwardMessage).
  // Цель резолвится (при первом обращении — создаётся) штатным механизмом.
  const forwardToFavorites = useCallback(async (ids: string[], closeDialog = false) => {
    if (ids.length === 0) return;
    setFavBusy(true);
    setError(null);
    try {
      const to = favoritesConversationId || (await ensureFavoritesChat(myId));
      for (const id of ids) await bridgeForward(conversationId, id, to);
      if (closeDialog) {
        setForwardIds([]);
        exitSelect();
      }
      useToastStore.getState().showSuccess(t('chat.forwarded_favorites'));
    } catch (err: unknown) {
      setError(friendlyError(err));
      logErrorToJournal(err, 'Чат: пересылка в «Избранное»');
    } finally {
      setFavBusy(false);
    }
  }, [conversationId, favoritesConversationId, myId, exitSelect]);

  // Копировать: тексты выбранных сообщений (lib/clipboard — SSoT копирования)
  const copySelected = async () => {
    const texts = messages
      .filter((m) => selectedIds.includes(m.id) && !!stripAssistantSources(m.text).trim())
      .map((m) => stripAssistantSources(m.text).trim());
    if (texts.length === 0) return;
    try {
      await copyToClipboard(texts.join('\n\n'));
      useToastStore.getState().showInfo(t('chat.copied_count', { count: texts.length }));
    } catch (e) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: копирование выбранных сообщений');
    }
  };

  // Удалить выбранные: как одиночное (scope 'self' — скрыть у себя), с подтверждением
  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const ok = await confirmDialog(selectedIds.length === 1
      ? t('chat.hide_one_confirm')
      : t('chat.hide_selected_confirm', { count: selectedIds.length }));
    if (!ok) return;
    setBulkBusy(true);
    setError(null);
    try {
      for (const id of selectedIds) await bridgeDelete(conversationId, id, 'self');
      exitSelect();
      void loadMessages(true);
    } catch (e) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: удаление выбранных сообщений');
    } finally {
      setBulkBusy(false);
    }
  };

  // Удалить ОДНО сообщение (ПКМ → «Удалить»): как одиночное скрытие у себя
  // (scope 'self', без восстановления), с подтверждением.
  const deleteOne = useCallback(async (m: Msg) => {
    const ok = await confirmDialog(t('chat.hide_single_confirm'));
    if (!ok) return;
    try {
      await bridgeDelete(conversationId, m.id, 'self');
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: удаление сообщения');
    }
  }, [conversationId]);

  // КОНТЕКСТ «СПРОСИТЬ АССИСТЕНТА»: сообщение (автор + текст + чат) уходит в
  // ЛИЧНЫЙ direct-чат с ассистентом — тот же чат, что и кнопка дока. Вложения
  // описываем текстом (ассистент видит их названия).
  const askAssistant = useCallback(async (m: Msg) => {
    const attachmentNames = (m.attachments || []).map((a) => a.name).filter(Boolean);
    const body = stripAssistantSources(m.text || '').trim() || (attachmentNames.length > 0 ? attachmentNames.join(', ') : '');
    if (!body) return;
    const author = m.senderId === myId
      ? t('chat.me')
      : (users.find((u) => u.id === m.senderId)?.name || t('chat.assistant_unknown_author'));
    try {
      // КОНТЕКСТ ОБЪЕКТА: в обсуждении записи ассистент получает тип, таблицу и
      // id — он может прочитать запись сам (read_object) и не переспрашивать.
      const contextLine = contextType && contextCode
        ? `\n${t('chat.ask_assistant_context', {
            type: OBJECT_CONTEXT_LABELS[contextType] || contextType,
            code: contextCode,
            id: contextId || '—',
          })}`
        : '';
      const prompt = `${t('chat.ask_assistant_prompt', { title: title || '—', author, text: body })}${contextLine}`;
      const { conversationId, queued } = await sendToAssistant(prompt);
      if (onOpenConversation) onOpenConversation(conversationId);
      else openConversation(conversationId);
      const toast = useToastStore.getState();
      if (queued) toast.showInfo(t('chat.ask_assistant_queued'));
      else toast.showSuccess(t('chat.ask_assistant_sent'));
    } catch (e: unknown) {
      const message = friendlyError(e);
      setError(message);
      useToastStore.getState().showError(message);
      logErrorToJournal(e, 'Чат: запрос ассистенту');
    }
  }, [myId, users, t, title, onOpenConversation, contextType, contextCode, contextId]);

  // Контекстное меню СООБЩЕНИЯ — единый набор действий для ПКМ и кнопки «…»
  // (кнопка нужна на сенсорных экранах: правого клика там нет):
  // ответить / переслать / закрепить / изменить (своё) / удалить + выбор/избранное.
  const openMessageMenu = useCallback((target: Msg, x: number, y: number) => {
    const items: AppMenuItem[] = [];
    if (!target.isDeleted) {
      items.push(
        { id: 'reply', label: t('chat.reply_action'), action: () => setReplyTo(target.id) },
        { id: 'forward', label: t('chat.forward_action'), action: () => forwardOne(target) },
        // Второстепенное действие панели над сообщением («Ещё» = это меню)
        { id: 'pin', label: target.isPinned ? t('chat.unpin') : t('chat.pin'), action: () => { void togglePin(target, !target.isPinned); } },
      );
      if (stripAssistantSources(target.text || '').trim() || (target.attachments || []).length > 0) {
        items.push({ id: 'ask-assistant', label: t('chat.ask_assistant'), action: () => void askAssistant(target) });
      }
      if (target.senderId === myId) {
        items.push({ id: 'edit', label: t('chat.edit_action'), action: () => { setEditMsg(target); setEditText(stripAssistantSources(target.text)); } });
      }
      items.push({ id: 'delete', label: t('chat.delete_action'), danger: true, action: () => void deleteOne(target) });
    }
    items.push({ id: 'select', label: t('chat.select_action'), separatorBefore: items.length > 0, action: () => enterSelect(target.id) });
    // Быстрое действие «В избранное» (заметки себе): пересылка одного сообщения
    // с вложениями; скрыто, когда открыт сам чат избранного.
    if (!target.isDeleted && !inFavoritesChat) {
      items.push({ id: 'favorites', label: t('chat.favorite'), action: () => void forwardToFavorites([target.id]) });
    }
    useAppContextMenu.getState().openMenu(x, y, items);
  }, [myId, deleteOne, forwardOne, togglePin, enterSelect, inFavoritesChat, forwardToFavorites, askAssistant]);
  // Жест удержания (тач/перо) вызывает актуальную версию меню.
  openMessageMenuRef.current = openMessageMenu;

  const onFeedContextMenu = (e: ReactMouseEvent) => {
    const id = messageIdAt(e.target);
    if (!id) return;
    e.preventDefault();
    const target = messages.find((m) => m.id === id);
    if (!target) return;
    openMessageMenu(target, e.clientX, e.clientY);
  };

  // «…» на бабле: то же меню, но открывается от кнопки (тач/клавиатура).
  const onMessageMore = useCallback((m: Msg, x: number, y: number) => {
    openMessageMenu(m, x, y);
  }, [openMessageMenu]);

  // Очистка чата (soft-delete, как в 1С): scope='self' — у себя; 'all' — пометка
  // «у всех» (любой участник); 'permanent' — физическое удаление (админ)
  const clearConv = async (scope: 'self' | 'all' | 'permanent') => {
    const ok = await confirmDialog(
      scope === 'self'
        ? t('chat.clear_self_confirm')
        : scope === 'all'
          ? t('chat.clear_all_confirm')
          : t('chat.clear_permanent_confirm'),
    );
    if (!ok) return;
    setError(null);
    try {
      await bridgeClear(conversationId, scope);
      // replace=true — ПОЛНАЯ перезагрузка ленты (очищенные сообщения исчезают,
      // а не мёржатся со старыми; иначе «очистка не работает»).
      void loadMessages(true);
    } catch (e: any) {
      setError(friendlyError(e));
    }
  };

  // Автозамена текстовых смайликов на эмодзи (при отправке)
  const autoEmoji = (t: string) => t
    .replace(/:\)/g, '😊').replace(/;\)/g, '😉').replace(/:\(/g, '😢')
    .replace(/:D|:-D/g, '😀').replace(/:\|/g, '😐').replace(/<3/g, '❤️')
    .replace(/:\){2,}/g, '😂').replace(/'\(/g, '😭').replace(/O_O|o_o/g, '😳');

  // ==========================================================================
  // ГОЛОСОВОЕ СООБЩЕНИЕ: запись через MediaRecorder (opus/webm) → аудио-
  // вложение. Расшифровка (STT) идёт ПАРАЛЛЕЛЬНО записи и попадает в текст
  // сообщения (Telegram-стиль: текст под аудио), В ПОЛЕ ВВОДА не пишется.
  // Ошибка/отсутствие STT — только аудио (без «не распознано» в теле).
  // ==========================================================================
  const [recording, setRecording] = useState(false);
  const [recSec, setRecSec] = useState(0);
  // Живая подпись под пузырём записи (interim заменяется целиком).
  const [sttLive, setSttLive] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sttSessionRef = useRef<SttSession | null>(null);
  const sttBufferRef = useRef(createTranscriptBuffer());
  // ЕДИНАЯ проверка доступности (SSoT): запись — только микрофон.
  const sttAvailability = useSttAvailability();
  const micAvailable = sttAvailability.canRecordVoice;

  const stopRecordingTimer = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  const stopVoiceStt = () => {
    sttSessionRef.current?.stop();
    sttSessionRef.current = null;
  };

  // Отмена записи голосового: останавливаем рекордер и STT, ЧИСТИМ чанки.
  const cancelRecording = () => {
    stopRecordingTimer();
    stopVoiceStt();
    sttBufferRef.current.reset();
    setSttLive('');
    setRecording(false);
    setAutoUpdateBusy('voice_recording', false);
    setRecSec(0);
    const mr = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    try { mr?.stream.getTracks().forEach((tr) => tr.stop()); } catch (e) {
      logSwallowed(e, 'components/chat/ChatThread.tsx:cancelRecording:stopTracks');
    }
    try { mr?.stop(); } catch (e) {
      logSwallowed(e, 'components/chat/ChatThread.tsx:cancelRecording:stopRecorder');
    }
    recChunksRef.current = [];
  };

  // Отправка голосового: аудио ВСЕГДА; текст расшифровки — если распознан.
  const deliverVoiceMessage = async (fileId: string, transcript: string) => {
    try {
      const r = await sendChatMessage({
        conversationId,
        text: transcript,
        attachments: [fileId],
        replyTo: replyTo || '',
      }, myId);
      if (r.queued) {
        useToastStore.getState().showInfo(t('chat.voice_offline_queued'));
      } else {
        void loadMessages();
      }
    } catch (e: any) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: отправка голосового сообщения');
    }
  };

  const startRecording = async () => {
    if (!micAvailable) {
      useToastStore.getState().showInfo(t('chat.voice_unavailable'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      recChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.start();
      // STT — только если доступен (микрофон уже разрешён жестом записи).
      sttBufferRef.current.reset();
      setSttLive('');
      if (sttAvailability.canTranscribe) {
        try {
          sttSessionRef.current = await startStt({
            continuous: true,
            onText: (chunk, isFinal) => {
              if (isFinal) sttBufferRef.current.addFinal(chunk);
              else sttBufferRef.current.setInterim(chunk);
              setSttLive(sttBufferRef.current.snapshot().display);
            },
            onError: (message) => {
              // STT необязателен: ошибка только в журнал, аудио продолжается.
              logSwallowed(new Error(message), 'components/chat/ChatThread.tsx:voiceStt');
            },
          });
        } catch (e) {
          logSwallowed(e, 'components/chat/ChatThread.tsx:voiceSttStart');
        }
      }
      setRecording(true);
      setAutoUpdateBusy('voice_recording', true);
      setRecSec(0);
      stopRecordingTimer();
      recTimerRef.current = setInterval(() => setRecSec((s) => s + 1), 1000);
    } catch (e: any) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: начало записи голосового');
    }
  };

  const stopRecording = async () => {
    const mr = mediaRecorderRef.current;
    stopRecordingTimer();
    if (!mr) { setRecording(false); setAutoUpdateBusy('voice_recording', false); return; }
    stopVoiceStt();
    const transcript = sttBufferRef.current.commit();
    sttBufferRef.current.reset();
    setSttLive('');
    setRecording(false);
    setAutoUpdateBusy('voice_recording', false);
    setRecSec(0);
    const stopped = new Promise<void>((resolve) => {
      mr.onstop = () => resolve();
      try { mr.stop(); } catch { resolve(); }
    });
    // Останавливаем треки микрофона (иначе индикатор «микрофон занят»)
    try { mr.stream.getTracks().forEach((tr) => tr.stop()); } catch (e) {
      logSwallowed(e, 'components/chat/ChatThread.tsx:stopRecording:stopTracks');
    }
    await stopped;
    const chunks = recChunksRef.current;
    recChunksRef.current = [];
    mediaRecorderRef.current = null;
    if (chunks.length === 0) return;
    const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
    const name = `voice-${Date.now()}.webm`;
    try {
      const file = new File([blob], name, { type: blob.type || 'audio/webm' });
      // ЕДИНЫЙ механизм загрузки (чанки + офлайн-очередь + прогресс).
      // Онлайн — отправляем сразу; офлайн — после догрузки (onComplete).
      let delivered = false;
      const deliver = (fileId: string) => {
        if (delivered || !fileId) return;
        delivered = true;
        void deliverVoiceMessage(fileId, transcript);
      };
      const job = await uploadFile(file, { onComplete: (_j, id) => deliver(id) });
      if (job.status === 'error') {
        setError(job.error || t('chat.voice_load_error'));
      } else if (job.status === 'queued') {
        useToastStore.getState().showInfo(t('chat.voice_offline_queued'));
      } else if (job.fileId) {
        deliver(job.fileId);
      }
    } catch (e: any) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: загрузка голосового');
    }
  };

  // Esc во время записи голосового = ОТМЕНА (очистить распознанный текст)
  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelRecording();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recording]);

  // Уход из чата во время записи: отпускаем микрофон и STT (без утечек).
  useEffect(() => () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    sttSessionRef.current?.stop();
    sttSessionRef.current = null;
    const mr = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    setAutoUpdateBusy('voice_recording', false);
    try { mr?.stream.getTracks().forEach((tr) => tr.stop()); } catch (e) {
      logSwallowed(e, 'components/chat/ChatThread.tsx:unmount:stopTracks');
    }
    try { mr?.stop(); } catch (e) {
      logSwallowed(e, 'components/chat/ChatThread.tsx:unmount:stopRecorder');
    }
  }, []);

  // Esc в режиме выбора сообщений = выход (как в мессенджерах)
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitSelect();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode, exitSelect]);

  const send = async () => {
    const messageText = autoEmoji(text.trim());
    if (!messageText && pendingFiles.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // ЕДИНАЯ точка отправки (offline-first): онлайн → сервер (ошибка
      // БРОСАЕТСЯ), офлайн → durable-очередь (flush при восстановлении связи)
      const r = await sendChatMessage({
        conversationId,
        text: messageText,
        attachments: pendingFiles.map((f) => f.id),
        replyTo: replyTo || '',
      }, myId);
      setText('');
      setPendingFiles([]);
      setReplyTo(null);
      if (r.queued) {
        useToastStore.getState().showInfo(t('chat.offline_send'));
      } else {
        void loadMessages();
      }
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Отправка из композера вложений: комментарий (+ уже набранный текст, если
  // он есть) и id загруженных файлов. ЕДИНАЯ точка — sendChatMessage
  // (online → сервер, offline → durable очередь сообщений).
  const sendAttachments = async (comment: string, fileIds: string[]) => {
    const body = autoEmoji([text.trim(), comment.trim()].filter(Boolean).join('\n\n'));
    const attachments = [...fileIds, ...pendingFiles.map((f) => f.id)];
    const r = await sendChatMessage({
      conversationId,
      text: body,
      attachments,
      replyTo: replyTo || '',
    }, myId);
    setText('');
    setPendingFiles([]);
    setReplyTo(null);
    setAttachComposer(null);
    if (r.queued) {
      useToastStore.getState().showInfo(t('chat.offline_send_attachments'));
    } else {
      void loadMessages();
    }
  };

  // Геометка (черновик): канонический ТЕКСТ через ЕДИНЫЙ механизм отправки
  // (offline-first chatQueue); серверный тип «geo» не поддержан — превью
  // рисуется локально (MessageBubble → GeoMessagePreview).
  const sendGeo = async (point: GeoMessagePoint) => {
    setGeoBusy(true);
    setError(null);
    try {
      const r = await sendChatMessage({
        conversationId,
        text: formatGeoMessage(point),
        attachments: [],
        replyTo: replyTo || '',
      }, myId);
      setGeoOpen(false);
      if (r.queued) {
        useToastStore.getState().showInfo(t('chat.offline_geo'));
      } else {
        void loadMessages();
      }
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setGeoBusy(false);
    }
  };

  const senderName = useCallback((id: string) => {
    if (id === myId) return t('chat.me');
    return users.find((u) => u.id === id)?.name || id.slice(0, 8);
  }, [myId, users]);

  // Пресенс: заголовок чата — подпись «онлайн/был(а)…» (для direct — собеседник)
  const other = memberLastSeen ? Object.keys(memberLastSeen).find((m) => m !== myId) : undefined;
  const presence = other && memberLastSeen ? lastSeenLabel(memberLastSeen[other] || '') : '';
  // Показываем «онлайн/был(а)» только для direct (другой участник); пустой last_seen —
  // показываем «не в сети» (иначе шапка без статуса «пустая»); группа — без подписи.
  // Для ботов/ассистента статус сети бессмыслен — подпись скрывается ниже (isBotChat).
  const isDirect = !!other && !!(memberLastSeen && Object.keys(memberLastSeen).length === 2);

  // Аватар собеседника для шапки (direct — другой участник)
  const otherUser = other ? users.find((u) => u.id === other) : undefined;

  // Собеседник direct-чата (SSoT состава диалога; memberLastSeen может быть
  // ещё не загружен — memberIds приходит из ChatPage).
  const assistantPeerId = useMemo(() => {
    const ids = (memberIds && memberIds.length > 0) ? memberIds : Object.keys(memberLastSeen || {});
    if (ids.length !== 2) return '';
    return ids.find((id) => id !== myId) || '';
  }, [memberIds, memberLastSeen, myId]);

  // Обычный пользователь НЕ видит ботов в listEntities (серверная видимость),
  // поэтому профиль собеседника direct-чата дорезолвиваем через единый сервис
  // контактов (логин + вид сущности). Это нужно и для распознавания ассистента,
  // и для того, чтобы у чата с ботом не показывать «Позвонить»/«Показать экран».
  const [resolvedPeer, setResolvedPeer] = useState<{ id: string; login: string; kind: string; isBot: boolean } | null>(null);
  useEffect(() => {
    if (!assistantPeerId) return;
    let alive = true;
    const run = async () => {
      try {
        const profile = await resolveEntityProfile(assistantPeerId);
        if (alive) setResolvedPeer({ id: assistantPeerId, ...profile });
      } catch (error) {
        logSwallowed(error, 'components/chat/ChatThread.tsx:assistant-peer');
      }
    };
    void run();
    return () => { alive = false; };
  }, [assistantPeerId]);

  // ЧАТ АССИСТЕНТА: прямой диалог с системным ботом «Мой ассистент».
  // Признак — СЛУЖЕБНЫЙ логин (стабилен при переименовании) либо имя чата
  // по умолчанию, которое задаёт ядро при создании бота.
  const assistantPeer = useMemo(() => {
    if (!assistantPeerId) return null;
    const live = users.find((u) => u.id === assistantPeerId);
    const resolved = resolvedPeer?.id === assistantPeerId ? resolvedPeer : null;
    const login = (live?.login || resolved?.login || '').trim();
    const kind = String(live?.kind || resolved?.kind || '').toLowerCase();
    const isBot = live?.isBot === true
      || resolved?.isBot === true
      || kind === 'bot' || kind === 'persona'
      || (!!login && isAssistantBotLogin(login));
    return { login, kind, isBot };
  }, [assistantPeerId, users, resolvedPeer]);

  const isAssistantChat = useMemo(() => {
    if (!assistantPeerId || !assistantPeer) return false;
    const { login, kind } = assistantPeer;
    if (login && isAssistantBotLogin(login)
      && ((kind === 'bot' || kind === 'persona') || login.toLowerCase() === ASSISTANT_BOT_LOGIN)) return true;
    const peerTitle = String(title || '').trim().toLowerCase();
    return peerTitle === 'мой ассистент' || peerTitle === 'ассистент';
  }, [assistantPeerId, assistantPeer, title]);

  // ЧАТ С БОТОМ/АССИСТЕНТОМ: кнопки звонка и показа экрана бессмысленны —
  // у бота нет ни LiveKit-участника, ни экрана. Прячем их в шапке и меню.
  const isBotChat = useMemo(() => {
    if (isAssistantChat) return true;
    if (!assistantPeer) return false;
    return assistantPeer.kind === 'bot' || assistantPeer.kind === 'persona' || assistantPeer.isBot;
  }, [isAssistantChat, assistantPeer]);

  // Подпись присутствия в шапке: у бота сети/последнего входа нет — статус
  // «не в сети» был бы бессмысленным. «Печатает…» (typingUser) остаётся.
  const presenceLabel = isBotChat
    ? ''
    : isDirect
      ? (presence || t('chat.offline_status'))
      : '';

  // Флаг для асинхронных загрузчиков ленты (фильтр удалённых в чате ассистента).
  useEffect(() => {
    isAssistantChatRef.current = isAssistantChat;
    // Переписка могла загрузиться ДО распознавания бота (переименованный бот):
    // убираем плейсхолдеры удалённых из уже загруженной ленты.
    if (isAssistantChat) setMessages((prev) => prev.filter((m) => !m.isDeleted));
  }, [isAssistantChat]);

  // «Слушать подряд» — единое действие для кнопки шапки и меню (узкий экран).
  const toggleAutoplay = () => {
    const next = !autoplayNext;
    setAutoplayNext(next);
    if (!next) handleAudioInterrupt();
    useToastStore.getState().showInfo(next ? t('chat.autoplay_on') : t('chat.autoplay_off'));
  };

  // НОВЫЙ ДИАЛОГ ассистента: серверная очистка чата (scope='all') + СБРОС
  // ПАМЯТИ диалога (BotResponder слушает chat.conversation.cleared — аналог /new).
  // Тот же чат и те же механизмы: второго мессенджера/чата не создаём.
  const startNewAssistantDialog = async () => {
    if (resetBusy) return;
    // Блокируем ПОВТОРНЫЙ вызов сразу (иначе два клика = два диалога подтверждения).
    setResetBusy(true);
    const ok = await confirmDialog(t('chat.assistant_new_dialog_confirm'));
    if (!ok) {
      setResetBusy(false);
      return;
    }
    setError(null);
    try {
      await bridgeClear(conversationId, 'all');
      // Лента очищается сразу (не ждём перезагрузки) — «Новый диалог» виден
      // мгновенно; потом сверяемся с сервером.
      setMessages([]);
      setInitialLoading(true);
      setReplyTo(null);
      setForwardIds([]);
      setThreadRoot(null);
      setText('');
      await loadMessages(true);
      useToastStore.getState().showSuccess(t('chat.assistant_new_dialog_done'));
    } catch (e) {
      const message = friendlyError(e);
      setError(message);
      logErrorToJournal(e, 'Чат ассистента: новый диалог');
    } finally {
      setResetBusy(false);
    }
  };

  // Меню действий шапки на УЗКОМ экране (мобильный/панель обсуждения):
  // второстепенные кнопки не выталкивают заголовок и поиск за пределы экрана.
  const openHeaderMenu = (x: number, y: number) => {
    const items: AppMenuItem[] = [];
    // Контекстная панель: у чата с ассистентом/ботом нет звонка, приглашения
    // по ссылке и показа экрана — эти пункты не предлагаем.
    if (!isBotChat) {
      items.push(
        { id: 'call', label: videoCallOpen ? t('chat.end_video_call') : t('chat.video_call'), action: startCall },
        { id: 'invite-link', label: t('chat.invite_link'), action: () => setInviteLinkOpen(true) },
        { id: 'remote', label: remoteOpen ? t('chat.remote_end') : t('chat.remote_show'), action: startRemote },
      );
    }
    items.push({
      id: 'autoplay',
      label: autoplayNext ? t('chat.autoplay_off_hint') : t('chat.autoplay_on_hint'),
      action: toggleAutoplay,
    });
    if (isAssistantChat) {
      items.push({ id: 'contacts', label: t('chat.contacts'), separatorBefore: true, action: () => { setContactsOpen(true); setInfoOpen(false); } });
      items.push({ id: 'new-dialog', label: t('chat.assistant_new_dialog'), action: () => void startNewAssistantDialog() });
    }
    items.push({ id: 'about', label: t('chat.about'), separatorBefore: !isAssistantChat, action: () => { setInfoOpen(true); setContactsOpen(false); } });
    items.push({ id: 'hide-self', label: t('chat.hide_self'), separatorBefore: true, action: () => void clearConv('self') });
    items.push({ id: 'hide-all', label: t('chat.hide_all'), action: () => void clearConv('all') });
    if (isAdmin) items.push({ id: 'permanent', label: t('chat.permanent'), danger: true, action: () => void clearConv('permanent') });
    useAppContextMenu.getState().openMenu(x, y, items);
  };

  // Контакты относятся к конкретному чату — при переключении закрываем панель.
  useEffect(() => {
    setContactsOpen(false);
  }, [conversationId]);

  // Подпись чипа пересылки: одиночная — текст сообщения, пакетная — количество
  const forwardLabel = forwardIds.length === 1
    ? (stripAssistantSources(messages.find((x) => x.id === forwardIds[0])?.text || '') || '📎 вложение')
    : `${forwardIds.length} сообщений`;

  // ЗАКРЕПЛЁННАЯ дата (как Telegram): текущая дата верхнего видимого
  // сообщения прилипает к верху ленты при скролле (onRangeChanged Virtuoso).
  const [topDate, setTopDate] = useState('');
  const dayLabel = (m: BubbleMsg) => {
    const d = new Date(m.createdAt);
    const today = new Date();
    const yest = new Date(); yest.setDate(yest.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Сегодня';
    if (d.toDateString() === yest.toDateString()) return 'Вчера';
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // СОХРАНЕНИЕ МЕСТА ЧТЕНИЯ: дебаунс 800 мс (settingsStore сам батчит запись
  // ещё 1 с) — настройки не пишутся на каждый пиксель/кадр скролла.
  const scheduleReadSave = useCallback(() => {
    if (!restoredRef.current) return;
    if (readSaveTimerRef.current) clearTimeout(readSaveTimerRef.current);
    readSaveTimerRef.current = setTimeout(() => {
      readSaveTimerRef.current = null;
      const pos = readPosRef.current;
      if (!pos) return;
      settingsStore.setJSON(readPosKey, { ...pos, updatedAt: new Date().toISOString() });
    }, 800);
  }, [readPosKey]);

  // При уходе из чата (unmount/смена диалога) — дописываем последнее место.
  useEffect(() => () => {
    if (readSaveTimerRef.current) {
      clearTimeout(readSaveTimerRef.current);
      readSaveTimerRef.current = null;
    }
    const pos = readPosRef.current;
    if (pos) settingsStore.setJSON(readPosKey, { ...pos, updatedAt: new Date().toISOString() });
  }, [readPosKey]);

  // ВОССТАНОВЛЕНИЕ МЕСТА ЧТЕНИЯ (как в Telegram): сохранённая позиция →
  // «у низа» / конкретное сообщение; иначе — первое непрочитанное / низ.
  // markRead позицию НЕ сбрасывает (он не участвует в сохранении места).
  useEffect(() => {
    if (messages.length === 0 || restoredRef.current) return;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      restoredRef.current = true;
      const lastIndex = messages.length - 1 + firstItemIndexRef.current;
      const saved = settingsStore.getJSON<ReadPos | null>(readPosKey, null);
      if (saved && typeof saved === 'object') readPosRef.current = saved;
      // Автоскролл включаем ТОЛЬКО после кадра восстановления — иначе
      // followOutput может увести вниз и перетереть сохранённую позицию.
      const scrollTo = (index: number, align: 'start' | 'end') => {
        requestAnimationFrame(() => {
          virtuosoRef.current?.scrollToIndex({ index, align });
          requestAnimationFrame(() => {
            suppressAutoScrollRef.current = false;
            didInitialScroll.current = true;
          });
        });
      };
      if (saved?.atBottom) {
        scrollTo(lastIndex, 'end');
        return;
      }
      if (saved?.id) {
        const idx = messages.findIndex((m) => m.id === saved.id);
        if (idx >= 0) {
          scrollTo(idx + firstItemIndexRef.current, 'start');
          return;
        }
      }
      // Нет сохранённой позиции (или сообщение вне загруженного окна) —
      // к первому непрочитанному, иначе к последнему сообщению.
      const unreadIdx = messages.findIndex((m) => !(m.readBy || []).includes(myId));
      if (unreadIdx > 0) scrollTo(unreadIdx + firstItemIndexRef.current, 'start');
      else scrollTo(lastIndex, 'end');
    };
    // Настройки могли ещё не гидратироваться (вход/смена базы) — ждём.
    if (settingsStore.isHydrated()) {
      apply();
    } else {
      void settingsStore.hydrate().then(() => { if (!cancelled) apply(); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, readPosKey, myId]);

  // КНОПКА «ВНИЗ» (jump-to-bottom, как в мессенджерах): показывается, когда
  // пользователь ушёл ОТ НИЖНЕГО КРАЯ (atBottom=false); с бейджем количества
  // НЕПРОЧИТАННЫХ (read_by без myId), который живёт до фактического прохода.
  // SSoT непрочитанного — сервер (read_by); здесь только UI-подсчёт.
  const [atBottom, setAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    atBottomRef.current = atBottom;
    if (atBottom) { setUnreadCount(0); return; }
    // При отходе от низа считаем непрочитанные (по текущему срезу сообщений);
    // по мере прочтения (read-события/новые) — пересчёт.
    setUnreadCount(messages.filter((m) => !(m.readBy || []).includes(myId)).length);
  }, [messages, myId, atBottom]);
  // Вернулись к нижнему краю — отмечаем прочитанным (позиция чтения при этом
  // не сбрасывается: она сохраняется по id видимого сообщения).
  const prevAtBottomRef = useRef(true);
  useEffect(() => {
    const was = prevAtBottomRef.current;
    prevAtBottomRef.current = atBottom;
    if (atBottom && !was && markReadOnOpen) {
      void bridgeMarkRead(conversationId).catch((e: unknown) => log.warn('markRead', { error: String((e as Error)?.message || e) }));
    }
  }, [atBottom, conversationId, markReadOnOpen]);
  const jumpToBottom = () => {
    virtuosoRef.current?.scrollToIndex({ index: Math.max(messages.length - 1, 0), align: 'end' });
    atBottomRef.current = true;
    readPosRef.current = { id: messages[messages.length - 1]?.id || '', atBottom: true };
    scheduleReadSave();
    if (markReadOnOpen) {
      void bridgeMarkRead(conversationId).catch((e: unknown) => log.warn('markRead', { error: String((e as Error)?.message || e) }));
    }
  };

  // ---- ПОИСК: переход к сообщению + подсветка ----------------------------
  // Сведения о вложениях ИЗ ЗАГРУЖЕННОЙ ленты. Основной источник — сервер
  // (SearchMessages отдаёт вложения), индекс ленты — резерв для старых
  // сообщений без серверных сведений.
  const attachmentIndex = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const m of messages) map.set(m.id, (m.attachments?.length || 0) > 0);
    return map;
  }, [messages]);

  const flashMessage = useCallback((messageId: string) => {
    setFlashMessageId(messageId);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setFlashMessageId('');
      flashTimerRef.current = null;
    }, 4000);
  }, []);
  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  // Переход к сообщению ГЛУБЖЕ загруженного окна: догружаем историю страницами
  // (before oldest), пока сообщение не появится, затем прокручиваем и
  // подсвечиваем. Сообщение старше (или удалено) — честное сообщение.
  const jumpToMessageDeep = useCallback(async (messageId: string, query = '') => {
    let all = messagesRef.current;
    let idx = all.findIndex((m) => m.id === messageId);
    let baseIndex = firstItemIndexRef.current;
    let pages = 0;
    while (idx < 0 && pages < 40) {
      const first = all[0];
      if (!first) break;
      let older: Msg[] = [];
      try {
        older = mapThreadMessages((await bridgeListMessagesBefore(conversationId, first.createdAt, 100)) as any[]);
      } catch (e: unknown) {
        log.warn('search jump: load older failed', { error: String((e as Error)?.message || e) });
        break;
      }
      if (older.length === 0) {
        setHasOlder(false);
        break;
      }
      all = [...older, ...all];
      baseIndex -= older.length;
      messagesRef.current = all;
      setFirstItemIndex((v) => v - older.length);
      // Не теряем сообщения, пришедшие ПОКА догружалась история: дописываем
      // их в конец (они новее загруженного окна).
      setMessages((prev) => {
        const known = new Set(all.map((m) => m.id));
        const newer = prev.filter((m) => !known.has(m.id));
        const next = newer.length
          ? [...all, ...newer].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          : all;
        messagesRef.current = next;
        return next;
      });
      idx = all.findIndex((m) => m.id === messageId);
      pages += 1;
    }
    if (idx < 0) {
      useToastStore.getState().showInfo(t('chat.message_not_found'));
      return;
    }
    setSearchHighlight(query);
    const target = idx + baseIndex;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: target, align: 'center' });
      flashMessage(messageId);
    }));
  }, [conversationId, flashMessage]);

  // Переход из внешнего поиска (другой чат / тот же чат): ждём первую загрузку.
  // Ref сбрасывается, когда родитель очистил фокус, — иначе ПОВТОРНЫЙ клик по
  // тому же результату поиска после прокрутки не срабатывал бы.
  const focusHandledRef = useRef('');
  useEffect(() => {
    if (!focusMessageId) focusHandledRef.current = '';
  }, [focusMessageId]);
  useEffect(() => {
    if (!focusMessageId || focusHandledRef.current === focusMessageId) return;
    if (messages.length === 0) return; // первичная загрузка ещё идёт
    focusHandledRef.current = focusMessageId;
    void jumpToMessageDeep(focusMessageId, focusQuery || '').finally(() => onFocusConsumed?.());
  }, [focusMessageId, focusQuery, messages.length, jumpToMessageDeep, onFocusConsumed]);

  // D6: переход к закреплённому сообщению из панели «О чате» — тот же глубокий
  // переход (может догрузить историю), а не только по загруженному окну.
  const jumpToMessage = useCallback((messageId: string) => {
    void jumpToMessageDeep(messageId, '');
  }, [jumpToMessageDeep]);
  // При получении нового сообщения, если пользователь У НИЗА — авто-проскролл
  // вниз (followOutput уже делает); если вверху — счётчик непрочитанных растёт
  // (пересчёт по эффекту [messages]).

  // Плашка «Непрочитанные» (как в Telegram): граница по read_by — первая
  // непрочитанная; рендерится в её элементе ленты и ВИДНА, пока пользователь
  // не у нижнего края (прочитал, сдвинувшись) — «пока не сдвинемся».
  const firstUnreadIdx = useMemo(() => {
    if (atBottom) return -1;
    return messages.findIndex((m) => !(m.readBy || []).includes(myId));
  }, [messages, atBottom, myId]);

  // Закреплённая дата просматриваемого фрагмента: ВСЕГДА видима и обновляется
  // при скролле (верхнее видимое сообщение). Оверлей ПОВЕРХ ленты (не элемент
  // Virtuoso — разделитель внутри виртуализации не может «прилипнуть»).
  const [stickyDate, setStickyDate] = useState('');
  const dateTextOf = (m: BubbleMsg): string => {
    const d = new Date(m.createdAt);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(yest.getDate() - 1);
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    if (day.getTime() === today.getTime()) return 'Сегодня';
    if (day.getTime() === yest.getTime()) return 'Вчера';
    const full = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
    const wd = d.toLocaleDateString('ru-RU', { weekday: 'short' });
    return `${wd.charAt(0).toUpperCase() + wd.slice(1)}, ${full}`;
  };

  // renderMessage — MEMO-компонент MessageBubble (плавность: Virtuoso ремонтирует
  // только изменённые баблы, не пересоздаёт все при скролле)
  const renderMessage = (idx: number, m: BubbleMsg) => {
    const prev = idx > 0 ? (messages[idx - 1] as any) : null;
    const src = m.replyTo ? messages.find((x) => x.id === m.replyTo) : undefined;
    const replySourceText = src ? `${src.senderId === myId ? 'Вы' : senderName(src.senderId)}: ${stripAssistantSources(src.text) || '📎 вложение'}` : undefined;
    const assistantAnswer = isAssistantChat && !!assistantPeerId && m.senderId === assistantPeerId;
    return (
      <>
        {/* Плашка «Непрочитанные» — над первой непрочитанной (read_by без myId);
            видна, пока пользователь НЕ у нижнего края (прочитал — сдвинулся) */}
        {!atBottom && idx === firstUnreadIdx && (
          <div className="flex items-center gap-2 px-3 pt-1 pointer-events-none">
            <span className="h-px flex-1" style={{ background: 'color-mix(in oklch, var(--color-primary) 45%, transparent)' }} />
            <span className="text-[9.5px] font-bold uppercase tracking-wider text-primary whitespace-nowrap">
              Непрочитанные
            </span>
            <span className="h-px flex-1" style={{ background: 'color-mix(in oklch, var(--color-primary) 45%, transparent)' }} />
          </div>
        )}
        <MessageBubble
          m={m}
          prevMsg={prev}
          myId={myId}
          isDark={isDark}
          senderName={senderName}
          avatarUrl={users.find((u) => u.id === m.senderId)?.avatarUrl}
          replySourceText={replySourceText}
          mentionLogins={mentionLogins}
          myLogin={myLogin}
          onReply={setReplyTo}
          onReactToggle={toggleReact}
          onForward={forwardOne}
          onEdit={(mm) => { setEditMsg(mm); setEditText(stripAssistantSources(mm.text)); }}
          onDelete={(mm) => { void deleteOne(mm); }}
          onMore={onMessageMore}
          onOpenThread={(mm) => { void openThread(mm); }}
          onPinToggle={(mm, pinned) => { void togglePin(mm, pinned); }}
          onDownload={handleAttachment}
          selectMode={selectMode}
          selected={selectMode && selectedIds.includes(m.id)}
          onToggleSelect={toggleSelect}
          autoPlayFileId={autoplayFileId}
          onAudioEnded={handleAudioEnded}
          onAudioInterrupt={handleAudioInterrupt}
          onAudioAutoplayConsumed={handleAutoplayConsumed}
          highlightQuery={m.id === flashMessageId ? searchHighlight : ''}
          highlighted={m.id === flashMessageId}
          assistantAnswer={assistantAnswer}
        />
        {/* ВСТРАИВАЕМЫЙ КОНТЕНТ по ссылке в сообщении (AppEmbed: youtube и др.;
            click-to-play — приватность, никаких авто-запросов; «Открыть в…»);
            в режиме выбора скрыт — тап по сообщению выбирает, а не открывает */}
        {!selectMode && !m.replyTo && m.text && <ChatEmbedLink text={stripAssistantSources(m.text)} />}
      </>
    );
  };

  return (
    // Desktop: «О чате» — КОЛОНКА справа (панель сжимает ленту, а не
    // перекрывает её), поэтому корень — строка: контент + панель.
    <div ref={rootRef} className="relative flex h-full min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* Шапка glass */}
      <div className="flex items-center gap-3 px-3 py-2 border-b shrink-0 glass-panel rounded-none border-x-0 border-t-0">
        {onBack && (
          <button className="p-1.5 rounded-full hover:bg-muted/40 -ml-1" onClick={onBack} aria-label={t('nav.back')}>
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {other ? (
          <UserAvatar uid={other} fileId={otherUser?.avatarUrl} name={otherUser?.name || title} size={36} />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground border shadow-sm shrink-0">
            <MessageSquare className="h-4 w-4" />
          </span>
        )}
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-semibold leading-none truncate">{title}</span>
              {(presenceLabel || typingUser) && (
                <span className={`block text-[12px] leading-none truncate mt-0.5 ${typingUser ? 'text-green-600 dark:text-green-400' : presenceLabel === 'онлайн' ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                  {typingUser ? `${typingUser} печатает…` : presenceLabel}
                </span>
              )}
            </span>
            {/* ВТОРОСТЕПЕННЫЕ ДЕЙСТВИЯ — на широком экране кнопками.
                На узком (телефон/панель обсуждения) они в меню «…»: иначе
                ряд иконок и текстовых кнопок выталкивает заголовок и поиск. */}
            {!narrowPanel && !isBotChat && (
              <>
                <button className="p-1.5 rounded-full hover:bg-muted/40" onClick={startCall} aria-label={t('chat.video_call')} title={videoCallOpen ? t('chat.end_video_call') : t('chat.video_call')}>
                  <Phone className="h-4 w-4" />
                </button>
                <button className="p-1.5 rounded-full hover:bg-muted/40" onClick={() => setInviteLinkOpen(true)} aria-label={t('chat.invite_link')} title={t('chat.invite_link')}>
                  <Link2 className="h-4 w-4" />
                </button>
                <button className="p-1.5 rounded-full hover:bg-muted/40" onClick={startRemote} aria-label={t('chat.remote')} title={remoteOpen ? t('chat.remote_end') : t('chat.remote_show')}>
                  <Monitor className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              className={`p-1.5 rounded-full hover:bg-muted/40 ${searchOpen ? 'bg-muted/60 text-primary' : ''}`}
              onClick={() => setSearchOpen((v) => !v)}
              aria-label={t('chat.search_messages')}
              aria-pressed={searchOpen}
              title={t('chat.search_messages')}
            >
              <Search className="h-4 w-4" />
            </button>
            {!narrowPanel && (
              <>
                {/* «Слушать подряд» (user_settings media.autoplay_next): следующее
                    голосовое/аудио ленты запускается автоматически после текущего;
                    выключение снимает и уже запланированный автозапуск. */}
                <button
                  className={`p-1.5 rounded-full hover:bg-muted/40 ${autoplayNext ? 'text-primary' : 'text-muted-foreground'}`}
                  onClick={toggleAutoplay}
                  aria-pressed={autoplayNext}
                  aria-label={t('chat.autoplay')}
                  title={autoplayNext ? t('chat.autoplay_on_hint') : t('chat.autoplay_off_hint')}
                >
                  <Headphones className="h-4 w-4" />
                </button>
                {isAssistantChat && (
                  <>
                    <button
                      className={`p-1.5 rounded-full hover:bg-muted/40 ${contactsOpen ? 'bg-muted/60 text-primary' : ''}`}
                      onClick={() => {
                        setContactsOpen((v) => !v);
                        setInfoOpen(false);
                      }}
                      aria-label={t('chat.contacts')}
                      aria-pressed={contactsOpen}
                      title={t('chat.contacts')}
                    >
                      <Users className="h-4 w-4" />
                    </button>
                    {/* НОВЫЙ ДИАЛОГ: очистка истории + сброс памяти разговора. */}
                    <button
                      className="p-1.5 rounded-full hover:bg-muted/40 disabled:opacity-40"
                      onClick={() => void startNewAssistantDialog()}
                      disabled={resetBusy}
                      aria-label={t('chat.assistant_new_dialog')}
                      title={t('chat.assistant_new_dialog_hint')}
                    >
                      <RotateCcw className={`h-4 w-4 ${resetBusy ? 'animate-spin' : ''}`} />
                    </button>
                  </>
                )}
                <button
                  className={`p-1.5 rounded-full hover:bg-muted/40 ${infoOpen ? 'bg-muted/60 text-primary' : ''}`}
                  onClick={() => {
                    setInfoOpen((v) => !v);
                    setContactsOpen(false);
                  }}
                  aria-label={t('chat.about')}
                  aria-pressed={infoOpen}
                  title={t('chat.about')}
                >
                  <Info className="h-4 w-4" />
                </button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => void clearConv('self')} title={t('chat.hide_self_hint')}>{t('chat.hide_self')}</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => void clearConv('all')} title={t('chat.hide_all_hint')}>{t('chat.hide_all')}</Button>
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive" onClick={() => void clearConv('permanent')} title={t('chat.permanent_hint')}>{t('chat.permanent')}</Button>
                )}
              </>
            )}
            {narrowPanel && (
              <button
                type="button"
                className="p-1.5 rounded-full hover:bg-muted/40"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  openHeaderMenu(r.right, r.bottom + 4);
                }}
                aria-label={t('chat.more_actions')}
                title={t('chat.more_actions')}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            )}
      </div>

      {/* ПОИСК (встроенная панель, без окон): этот чат / все чаты, фильтры
          по дате/отправителю/вложениям, подсветка совпадений и переход к
          сообщению (с догрузкой истории при необходимости). */}
      {searchOpen && (
        <ChatSearchPanel
          users={users}
          conversationId={conversationId}
          memberIds={mentionMembers}
          defaultScope="chat"
          allowScopeSwitch
          autoFocus
          attachmentIndex={attachmentIndex}
          onClose={() => setSearchOpen(false)}
          onOpen={(hit, q) => {
            if (hit.conversationId && hit.conversationId !== conversationId) {
              onOpenMessage?.(hit.conversationId, hit.id, q);
              return;
            }
            void jumpToMessageDeep(hit.id, q);
            // На узком экране панель занимает половину высоты — закрываем,
            // чтобы найденное сообщение было видно целиком.
            if (narrowPanel) setSearchOpen(false);
          }}
        />
      )}

      {/* Режим выбора сообщений (§2.3): счётчик + Копировать/Переслать/Удалить/Отмена */}
      {selectMode && (
        <ChatSelectionBar
          count={selectedIds.length}
          canCopy={messages.some((m) => selectedIds.includes(m.id) && !!stripAssistantSources(m.text).trim())}
          showForward={!!forwardTargets && forwardTargets.length > 0}
          busy={bulkBusy}
          onCopy={() => void copySelected()}
          onForward={() => setForwardIds([...selectedIds])}
          onDelete={() => void deleteSelected()}
          onCancel={exitSelect}
        />
      )}

      {/* Видеозвонок (WebRTC/LiveKit): полноэкранный экран; комната = conv_{conversation_id} */}
      {videoCallOpen && (
        <VideoCallPanel
          roomName={`conv_${conversationId}`}
          identity={myId}
          myName={myName}
          callId={videoCallId || undefined}
          onLeave={() => setVideoCallOpen(false)}
          getInviteTarget={async () => ({ conversationId, title })}
        />
      )}
      {/* Ссылка-приглашение в ЭТОТ чат */}
      {inviteLinkOpen && (
        <InviteLinkDialog conversationId={conversationId} title={title} onClose={() => setInviteLinkOpen(false)} />
      )}

      {/* Удалённый доступ: просмотр экрана собеседника (комната remote_chat-{conversation_id}) */}
      {remoteOpen && (
        <RemotePanel sessionId={`chat-${conversationId}`} role="viewer" hostName={title} source="chat" onLeave={() => setRemoteOpen(false)} />
      )}

      {/* Офлайн: лента показана из сохранённого кэша */}
      {chatOffline && (
        <div className="px-2 pt-1.5 shrink-0">
          <OfflineDataBanner sourceKey={chatOfflineKey} />
        </div>
      )}

      {/* Лента — Virtuoso (виртуализация, миллионы сообщений, плавный скролл) */}
      {messages.length === 0 && !error && initialLoading && (
        <div className="flex-1 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />{t('chat.loading')}
        </div>
      )}
      {messages.length === 0 && !error && !initialLoading && isAssistantChat && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <Sparkles className="h-8 w-8 text-primary/70" />
          <div className="text-sm font-medium">{t('chat.assistant_empty_title')}</div>
          <p className="max-w-sm text-[11px] text-muted-foreground">{t('chat.assistant_empty_hint')}</p>
          <div className="flex flex-wrap justify-center gap-1.5">
            {(['chat.assistant_suggest_1', 'chat.assistant_suggest_2', 'chat.assistant_suggest_3'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className="rounded-full border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                onClick={() => { setText(t(key)); mentionInputRef.current?.focus(); }}
              >
                {t(key)}
              </button>
            ))}
          </div>
        </div>
      )}
      {messages.length === 0 && !error && !initialLoading && !isAssistantChat && (
        <div className="flex-1 flex items-center justify-center text-[10px] text-muted-foreground italic p-3">{t('chat.no_messages')}</div>
      )}
      {messages.length === 0 && error && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <span className="text-xs text-destructive">{error}</span>
          <Button size="sm" variant="outline" onClick={() => { setInitialLoading(true); void loadMessages(true); }}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />{t('common.retry')}
          </Button>
        </div>
      )}
      {messages.length > 0 && (
        <div
          className="relative flex-1 min-h-0 overflow-hidden"
          style={chatBg ? {} : undefined}
          onClick={onFeedClickOutside}
          onContextMenu={onFeedContextMenu}
          onPointerDown={onFeedPointerDown}
          onPointerMove={onFeedPointerMove}
          onPointerUp={onFeedPointerUp}
          onPointerCancel={onFeedPointerCancel}
        >
          {/* Закреплённая дата ПРОСМАТРИВАЕМОГО фрагмента — оверлей ПОВЕРХ ленты:
              всегда видна и меняется при скролле (дата верхнего видимого) */}
          {stickyDate && (
            <div className="pointer-events-none absolute top-2 left-0 right-0 z-20 flex justify-center">
              <span className="text-[10px] font-semibold text-muted-foreground bg-sidebar/70 backdrop-blur-md border border-border/60 rounded-full px-2.5 py-1 shadow-sm">
                {stickyDate}
              </span>
            </div>
          )}
          <div
            className={`absolute inset-0 ${chatBg ? '' : 'chat-premium-bg'}`}
            style={chatBg ? { backgroundImage: chatBg, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
          />
          <Virtuoso
            ref={virtuosoRef}
            data={messages}
            computeItemKey={(_i, m) => m.id}
            firstItemIndex={firstItemIndex}
            className="chat-scroll relative h-full w-full overflow-x-hidden"
            atTopStateChange={(atTop) => { if (atTop) void loadOlder(); }}
            rangeChanged={(range: ListRange) => {
              // Верхнее видимое → закреплённая дата.
              // react-virtuoso при firstItemIndex даёт range.startIndex в АБСОЛЮТНЫХ
              // координатах (>= firstItemIndex). Изменение data (loadOlder) идёт
              // батчем с firstItemIndex — в этот момент границы могут на миг
              // «съехать»; поэтому НЕ сбрасываем дату при выходе за границы
              // (сохраняем предыдущую, пока не придёт валидный rel).
              const rel = range.startIndex - firstItemIndex;
              if (rel >= 0 && rel < messages.length) {
                const m = messages[rel];
                if (m) setStickyDate(dateTextOf(m));
                // МЕСТО ЧТЕНИЯ: верхнее видимое сообщение (дебаунс сохранения).
                if (m && !suppressAutoScrollRef.current) {
                  readPosRef.current = { id: m.id, atBottom: false };
                  scheduleReadSave();
                }
              }
              // «У низа» — последние 3 сообщения в кадре (для кнопки «вниз»).
              const endRel = range.endIndex - firstItemIndex;
              const bottom = endRel >= messages.length - 3;
              setAtBottom(bottom);
              atBottomRef.current = bottom;
              if (bottom && !suppressAutoScrollRef.current) {
                const last = messages[messages.length - 1];
                readPosRef.current = { id: last?.id || readPosRef.current?.id || '', atBottom: true };
                scheduleReadSave();
              }
            }}
            followOutput={(isAtBottom) => {
              // До восстановления места чтения автоскролл выключен — иначе
              // followOutput уводит вниз и сохранённая позиция теряется.
              if (suppressAutoScrollRef.current) return false;
              if (!didInitialScroll.current && messages.length > 0) {
                didInitialScroll.current = true;
                return 'auto';
              }
              return isAtBottom ? 'smooth' : false;
            }}
            increaseViewportBy={{ top: 400, bottom: 600 }}
            itemContent={(index, m) => renderMessage(index - firstItemIndex, m)}
            components={{
              Header: () => (
                <div className={`flex justify-center items-center h-6 transition-opacity ${loadingOlder ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                  <span className="text-[10px] text-muted-foreground italic animate-pulse">{t('chat.loading')}</span>
                </div>
              ),
              Footer: () => <div className="h-2" />,
            }}
          />
          {/* КНОПКА «ВНИЗ» + бейдж непрочитанных (как в мессенджерах): видна,
              когда ушли от низа; клик — вниз; бейдж = N непрочитанных */}
          {!atBottom && (
            <div className="absolute bottom-2.5 right-3 z-20 flex flex-col items-center gap-1">
              {unreadCount > 0 && (
                <span className="pointer-events-none text-[9.5px] font-semibold text-primary-foreground bg-primary rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-sidebar/80 backdrop-blur-md shadow-md hover:bg-accent transition-colors"
                title={t('chat.jump_bottom')}
                onClick={jumpToBottom}
              >
                <ArrowDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          )}
        </div>
      )}

      {error && messages.length > 0 && <div className="px-3 py-1 text-[10px] text-destructive bg-destructive/10 border-t border-destructive/20">{error}</div>}

      {/* Индикатор офлайн-очереди: N сообщений этого чата ожидают отправки */}
      <QueuedIndicator conversationId={conversationId} />
      {/* Ввод glass — shrink-0: НЕ даём flex-колонке сжать поле ввода до нуля
          (лента flex-1 могла «съесть» всю высоту на мобиле → ввод уезжал
          за нижний край и был невидим) */}
      <div className="p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t glass-panel rounded-none border-x-0 border-b-0 flex flex-col gap-1.5 shrink-0">
        {(replyTo || forwardIds.length > 0) && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground border rounded-full px-3 py-1 bg-muted/30">
            {replyTo && (() => {
              const src = messages.find((x) => x.id === replyTo);
              return <span className="truncate flex-1">↩️ В ответ на: {src ? `${src.senderId === myId ? 'Вы' : senderName(src.senderId)}: ${stripAssistantSources(src.text) || '📎 вложение'}` : 'сообщение'}</span>;
            })()}
            {forwardIds.length > 0 && <span className="truncate flex-1">{t('chat.forwarding_prefix', { label: forwardLabel })}</span>}
            <button className="shrink-0 p-1 rounded-full hover:bg-muted/50" onClick={() => { setReplyTo(null); setForwardIds([]); }} aria-label={t('chat.cancel_reply')}>
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pendingFiles.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1.5 text-[11px] border rounded-full px-2.5 py-1 bg-muted/20">
                📎 {f.name}
                <button className="p-0.5 rounded-full hover:bg-muted/50" onClick={() => setPendingFiles((p) => p.filter((x) => x.id !== f.id))} aria-label={t('chat.remove')}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* Пузырь записи + ЖИВАЯ подпись распознавания ПОД ним (в поле ввода
            текст не попадает). Interim заменяется целиком, final накапливается. */}
        {recording && (
          <div className="px-0.5 flex flex-col items-start gap-1" data-voice-recording>
            <div
              className="inline-flex items-center gap-2 max-w-[70%] rounded-2xl px-3 py-1.5 text-[12px] chat-bubble-mine"
              title={t('chat.recording')}
            >
              <Mic className="h-3.5 w-3.5 animate-pulse" />
              <span className="font-semibold">{recSec}s</span>
            </div>
            {sttLive && (
              <div className="max-w-full pl-2 pr-2 text-[11px] italic leading-snug text-muted-foreground line-clamp-2 [overflow-wrap:anywhere]">
                {sttLive}
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <div className="flex-1 flex items-center gap-1 glass-input rounded-full px-2 py-1 relative">
            <EmojiPicker onPick={(e) => { const v = text + e; setText(v); mention.onChange(v, v.length); }} />
            <button
              type="button"
              className="h-8 w-8 rounded-full hover:bg-muted/40 flex items-center justify-center shrink-0"
              title={t('chat.attach')}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => void pickFiles(e)} />
            <button
              type="button"
              className="h-8 w-8 rounded-full hover:bg-muted/40 flex items-center justify-center shrink-0"
              title={t('chat.geo')}
              onClick={() => setGeoOpen(true)}
            >
              <MapPin className="h-4 w-4" />
            </button>
            <Input
              ref={mentionInputRef}
              value={text}
              onChange={(e) => { const v = e.target.value; setText(v); mention.onChange(v, e.target.selectionStart); sendTyping(); }}
              onKeyDown={(e) => {
                // Автодополнение «@»: ↓/↑/Enter/Tab/Esc обрабатывает список,
                // остальное уходит дальше (Enter — отправка сообщения)
                if (mention.onKeyDown(e)) return;
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
              }}
              onBlur={mention.onBlur}
              onPaste={(e) => void handlePaste(e)}
              placeholder={t('chat.message')}
              className="h-8 text-[13px] flex-1 min-w-0 border-0 shadow-none focus-visible:ring-0 bg-transparent px-2"
            />
            {/* Автодополнение @ — список УЧАСТНИКОВ диалога над полем ввода */}
            {mention.open && (
              <ChatMentionSuggest
                items={mention.items}
                activeIndex={mention.activeIndex}
                query={mention.query}
                onHover={mention.hover}
                onPick={mention.pick}
              />
            )}
            {recording && (
              <>
                <span className="text-[11px] font-semibold text-destructive shrink-0">{recSec}s</span>
                <button
                  type="button"
                  className="h-8 w-8 rounded-full hover:bg-muted/40 flex items-center justify-center shrink-0"
                  title={t('chat.cancel_recording')}
                  onClick={cancelRecording}
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            )}
            {/* КОНТЕКСТНАЯ кнопка ВНУТРИ поля (как Telegram): есть текст →
                «Отправить»; текст пуст → «Голосовое»; запись идёт → «Стоп».
                Микрофон запрещён/недоступен → голосовой кнопки НЕТ. */}
            {(recording || !!text.trim() || micAvailable) && (
              <button
                type="button"
                className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  recording
                    ? 'bg-destructive text-white animate-pulse'
                    : 'bg-primary text-primary-foreground hover:opacity-90'
                }`}
                title={text.trim() ? 'Отправить сообщение (Enter)' : recording ? 'Остановить запись' : 'Записать голосовое сообщение'}
                onClick={() => {
                  if (recording) { void stopRecording(); return; }
                  if (text.trim()) { void send(); return; }
                  void startRecording();
                }}
              >
                {recording ? <Square className="h-3.5 w-3.5" /> : text.trim() ? <Send className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Композер вложений: мобильный bottom sheet / десктопное окно.
          Портал в body — раскладка ленты и поля ввода чата не сжимается. */}
      {attachComposer && (
        <ChatAttachmentComposer
          files={attachComposer.files}
          warnings={attachComposer.warnings}
          extraCount={pendingFiles.length}
          onCancel={() => setAttachComposer(null)}
          onSend={sendAttachments}
        />
      )}

      {/* D3: Обсуждение (тред) — корень + ответы; ответ уходит в тред (reply_to) */}
      {threadRoot && (
        <UniversalWindow open onClose={() => setThreadRoot(null)}
          title={`Обсуждение · ${(stripAssistantSources(threadRoot.text) || 'вложение').slice(0, 40)}`}
          width="w-[min(560px,94vw)]" resizable={false}>
          <div className="flex flex-col" style={{ height: '60vh' }}>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {threadItems.map((it) => (
                <div key={it.id} className="rounded-md border p-2" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="text-[10px] text-muted-foreground">
                    {senderName(it.senderId)}{it.id === threadRoot.id ? ' · начало обсуждения' : ''}
                  </div>
                  <div className="text-[12.5px] whitespace-pre-wrap break-words">
                    {it.isDeleted ? 'Сообщение удалено' : stripAssistantSources(it.text)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(it.createdAt).toLocaleString('ru-RU')}{it.editedAt ? ' · изменено' : ''}
                  </div>
                </div>
              ))}
              {threadItems.length <= 1 && (
                <div className="text-[11px] text-muted-foreground px-1">{t('chat.no_replies')}</div>
              )}
            </div>
            <div className="border-t p-2 flex items-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
              <textarea
                className="flex-1 min-h-12 max-h-32 rounded-md border bg-background p-2 text-sm leading-snug resize-y"
                value={threadText}
                onChange={(e) => setThreadText(e.target.value)}
                placeholder={t('chat.reply')}
              />
              <Button size="sm" onClick={sendThread} disabled={threadBusy || !threadText.trim()}>
                {threadBusy ? '…' : t('chat.reply_send')}
              </Button>
            </div>
          </div>
        </UniversalWindow>
      )}

      {/* D2: Изменить сообщение (своё; история правок сохраняется на сервере) */}
      {editMsg && (
        <UniversalWindow open onClose={() => { if (!editBusy) setEditMsg(null); }} title={editMsg.attachments?.some((a) => isVoiceMessage(a.name, a.fileType)) ? t('chat.edit_transcript_title') : t('chat.edit_title')} blocking width="w-[min(560px,94vw)]" resizable={false}>
          <div className="p-2 space-y-2">
            <textarea
              className="w-full min-h-24 rounded-md border bg-background p-2 text-sm leading-snug resize-y"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              autoFocus
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{t('chat.edit_history')}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditMsg(null)} disabled={editBusy}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={doEdit} disabled={editBusy || !editText.trim()}>
                  {editBusy ? t('chat.saving') : t('common.save')}
                </Button>
              </div>
            </div>
          </div>
        </UniversalWindow>
      )}

      {/* Переслать: выбор чата (одиночная и пакетная — единый диалог) */}
      {forwardIds.length > 0 && (
        <UniversalWindow open onClose={() => setForwardIds([])} title={forwardIds.length === 1 ? t('chat.forward_title') : t('chat.forward_many', { count: forwardIds.length })} blocking width="max-w-sm" resizable={false}>          <div className="p-2 space-y-1 max-h-96 overflow-auto">
            {/* БЫСТРОЕ ДЕЙСТВИЕ: «В избранное» (заметки себе) — в один клик;
                цель — чат «только я» (резолвится/создаётся при первом обращении) */}
            {!inFavoritesChat && (
              <button
                type="button"
                disabled={favBusy}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-accent text-xs flex items-center gap-2 border-b border-border/40 pb-2 mb-1"
                onClick={() => void forwardToFavorites(forwardIds, true)}
              >
                <Star className="h-3 w-3 shrink-0 text-primary" />
                <span className="truncate font-medium">{t('chat.favorite')}</span>
                {favBusy && <span className="ml-auto text-[10px] text-muted-foreground">{t('chat.forwarding')}</span>}
              </button>
            )}
            {(!forwardTargets || forwardTargets.length === 0) && inFavoritesChat && (
              <div className="text-xs text-muted-foreground p-2">{t('chat.other_empty')}</div>
            )}
            {(forwardTargets || []).map((c) => (
              <button
                key={c.id}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-accent text-xs flex items-center gap-2"
                onClick={() => void doForward(c.id)}
              >
                <MessageSquare className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{c.title}</span>
              </button>
            ))}
          </div>
        </UniversalWindow>
      )}

      {geoOpen && (
        <GeoMessageComposer onClose={() => setGeoOpen(false)} onSend={sendGeo} sending={geoBusy} />
      )}

      </div>

      {/* Правая панель «О чате» (D6): сущность/участники/закреплённые/файлы.
          Desktop — статичная колонка (см. ChatInfoPanel), mobile — полноэкранная. */}
      <ChatInfoPanel
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        conversationId={conversationId}
        title={title}
        myId={myId}
        users={users}
        memberIds={memberIds}
        memberLastSeen={memberLastSeen}
        contextType={contextType}
        contextCode={contextCode}
        contextId={contextId}
        messages={messages}
        onPreview={handleAttachment}
        onOpenConversation={onOpenConversation ?? ((id) => openConversation(id))}
        onJumpToMessage={jumpToMessage}
        isAdmin={isAdmin}
        overlay={narrowPanel}
        onLeftConversation={onConversationLeft}
      />

      {/* Контакты пользователя в чате ассистента: единый каталог и карточка
          контакта (Telegram/MAX/внутренний чат/профиль). Панель и «О чате»
          взаимоисключающие — см. кнопки в шапке. */}
      {isAssistantChat && (
        <AssistantContactsPanel
          key={conversationId}
          open={contactsOpen}
          onClose={() => setContactsOpen(false)}
          myId={myId}
          isAdmin={isAdmin}
        />
      )}

      {previewAtt && (
        <FilePreviewModal
          fileId={previewAtt.id}
          fileName={previewAtt.name}
          onClose={() => setPreviewAtt(null)}
        />
      )}
    </div>
  );
}

// Извлечь ПЕРВУЮ http(s)-ссылку из текста (для AppEmbed-превью под сообщением).
import { ChatEmbedLink } from './chatEmbed';
